// Linked-target resolution for `gundi:reference` annotations whose `target`
// is "provider" or "destination". See:
//   docs/superpowers/specs/2026-07-31-reference-data-config-ui-design.md §3
//     ("Provider resolution")
//   docs/superpowers/specs/2026-10-08-reference-destination-target-design.md
//   src/api/referenceOptions.ts (reused here for the per-target fetches)
//
// For target: "provider", the field's options come from the integration(s)
// on the other side of the Route(s) in which the configured integration
// is the *destination* — not from the integration being configured itself.
// For target: "destination", the mirror: the configured integration is the
// *provider*, and the options come from the destination(s) of its
// connection. Either way a target only counts if its integration *type*
// registered the named reference action. Targets lacking it are skipped; if
// none has it, the caller (ReferenceSelectWidget) falls back to free text.
import { createAxiosInstance, getQueryString, filter_page_size } from "./apiUtils";
import { fetchReferenceOptions, ReferenceDataResponse, ReferenceOption } from "./referenceOptions";

// --- Per-integration details cache ------------------------------------------
// Mirrors referenceOptions.ts's cache shape: cache the in-flight promise
// immediately (dedupes concurrent lookups for the same integration), then keep
// the resolved value cached for the TTL. Evict on failure so a transient
// error doesn't wedge a target out of consideration for 5 minutes. Shared by
// both directions: a destination's details are the same record whichever
// side asks.
type DetailsCacheEntry = { expiresAt: number; promise: Promise<any> };
const detailsCache = new Map<string, DetailsCacheEntry>();

const PROVIDER_DETAILS_TTL_MS = 300 * 1000;
const PROVIDER_DETAILS_INFLIGHT_TTL_MS = 15000;

// --- Resolved-targets cache --------------------------------------------------
// The Routes / connection lookup below was the one call here no cache covered,
// so every menu open (and every row of a mapping array) re-asked it. Same shape
// as the details cache above. Keyed by direction: with bidirectional routes
// the same integration can be a provider in one route and a destination in
// another, so the two directions must never share an entry. An empty
// resolution is not cached (see cachedTargets).
type TargetsCacheEntry = { expiresAt: number; promise: Promise<string[]> };
const targetsCache = new Map<string, TargetsCacheEntry>();

export function clearProviderTargetsCache() { // tests
    detailsCache.clear();
    targetsCache.clear();
}

function fetchIntegrationDetailsCached(token: string, integrationId: string): Promise<any> {
    const hit = detailsCache.get(integrationId);
    if (hit && hit.expiresAt > Date.now()) return hit.promise;
    const promise = (async () => {
        const axiosInstance = await createAxiosInstance(token);
        const resp = await axiosInstance.get(`/v2/integrations/${encodeURIComponent(integrationId)}/`);
        const data = resp.data;
        detailsCache.set(integrationId, { expiresAt: Date.now() + PROVIDER_DETAILS_TTL_MS, promise: Promise.resolve(data) });
        return data;
    })();
    detailsCache.set(integrationId, { expiresAt: Date.now() + PROVIDER_DETAILS_INFLIGHT_TTL_MS, promise });
    promise.catch(() => { if (detailsCache.get(integrationId)?.promise === promise) detailsCache.delete(integrationId); });
    return promise;
}

/** True iff the integration's type registered `actionValue` as a reference action. */
function supportsReferenceAction(integrationDetails: any, actionValue: string): boolean {
    const actions = integrationDetails?.type?.actions;
    if (!Array.isArray(actions)) return false;
    return actions.some((a: any) => a && a.value === actionValue && a.type === "reference");
}

function cachedTargets(key: string, resolve: () => Promise<string[]>): Promise<string[]> {
    const hit = targetsCache.get(key);
    if (hit && hit.expiresAt > Date.now()) return hit.promise;
    const promise = resolve();
    targetsCache.set(key, { expiresAt: Date.now() + PROVIDER_DETAILS_INFLIGHT_TTL_MS, promise });
    // Registered before the caller can await, so the cache is settled by the
    // time the caller's continuation runs.
    promise.then(
        (resolved) => {
            if (targetsCache.get(key)?.promise !== promise) return;
            if (resolved.length > 0) {
                targetsCache.set(key, { expiresAt: Date.now() + PROVIDER_DETAILS_TTL_MS, promise: Promise.resolve(resolved) });
            } else {
                // An empty answer is the one that changes under the user: the
                // route wizard sends them to the flow map to attach a
                // destination and promises the dropdown fills on the next
                // edit. Keep asking until there is something to remember.
                targetsCache.delete(key);
            }
        },
        () => { if (targetsCache.get(key)?.promise === promise) targetsCache.delete(key); },
    );
    return promise;
}

/** Keep the ids whose integration type registers `actionValue` as a reference
 * action. A single integration whose details can't be fetched is skipped
 * (best-effort), never fatal. */
async function filterByReferenceSupport(token: string, ids: string[], actionValue: string): Promise<string[]> {
    const checked = await Promise.all(
        ids.map(async (id) => {
            try {
                const details = await fetchIntegrationDetailsCached(token, id);
                return supportsReferenceAction(details, actionValue) ? id : null;
            } catch {
                return null;
            }
        }),
    );
    return checked.filter((id): id is string => id !== null);
}

function uniqueIds(values: unknown[]): string[] {
    return Array.from(
        new Set(values.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)),
    );
}

/** Resolve the provider integration ids that (a) are on the other side of a
 * Route where `destinationIntegrationId` is the destination, and (b)
 * whose integration type registers `actionValue` as a reference action.
 * Fetches Routes in a single, generously-limited page (per the spec:
 * "resolve the Routes in which the integration is a destination").
 * A failure fetching the Routes list itself propagates to the caller. */
export function resolveProviderTargets(
    token: string,
    destinationIntegrationId: string,
    actionValue: string,
): Promise<string[]> {
    // Not keyed by token: a new session remounts the widget anyway.
    return cachedTargets(`provider::${destinationIntegrationId}::${actionValue}`, async () => {
        const axiosInstance = await createAxiosInstance(token);
        const query_string = getQueryString({ destination_id: destinationIntegrationId });
        const resp = await axiosInstance.get(
            `/v2/connections/?pager=limit&limit=${filter_page_size}&${query_string}`,
        );
        const routes: any[] = resp.data?.results ?? [];
        const providerIds = uniqueIds(routes.map((route) => route?.provider?.id));
        return filterByReferenceSupport(token, providerIds, actionValue);
    });
}

/** Resolve the destination integration ids of the connection whose provider
 * is `providerIntegrationId` and whose integration type registers
 * `actionValue` as a reference action. The connection details endpoint is
 * the same lookup an action runner makes to find its destinations, so the
 * two always agree on which integrations are in play. Its destination
 * summaries carry no type actions, so each destination's details are fetched
 * (cached) for the capability check, exactly as the provider path does.
 * A 404 means the integration is not a provider on any route yet and resolves
 * to no targets; any other failure fetching the connection propagates. */
export function resolveDestinationTargets(
    token: string,
    providerIntegrationId: string,
    actionValue: string,
): Promise<string[]> {
    return cachedTargets(`destination::${providerIntegrationId}::${actionValue}`, async () => {
        const axiosInstance = await createAxiosInstance(token);
        let resp: any;
        try {
            resp = await axiosInstance.get(`/v2/connections/${encodeURIComponent(providerIntegrationId)}/`);
        } catch (error: any) {
            // The connections endpoint only serves integrations that are a
            // provider on at least one route, so a brand-new provider 404s.
            // That is "no destination yet", not a failure to retry.
            if (error?.response?.status === 404) return [];
            throw error;
        }
        const destinations: any[] = resp.data?.destinations ?? [];
        const destinationIds = uniqueIds(destinations.map((destination) => destination?.id));
        return filterByReferenceSupport(token, destinationIds, actionValue);
    });
}

/** Fan the reference action out to each target (via fetchReferenceOptions, so
 * per-target results are TTL-cached and in-flight-deduped exactly like the
 * self-target path) and union the results deduped by option value (first
 * occurrence wins). `targetCount` is the number of targets that matched (not
 * the number that successfully returned options) — the widget uses
 * targetCount === 0 to decide whether to fall back to free text. */
async function fetchLinkedReferenceOptions(
    token: string,
    targetIds: string[],
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { targetCount: number }> {
    if (targetIds.length === 0) {
        return { options: [], cache_ttl_seconds: 300, truncated: false, targetCount: 0 };
    }

    const settled = await Promise.allSettled(
        targetIds.map((targetId) => fetchReferenceOptions(token, targetId, actionValue, params)),
    );

    const successes = settled.filter(
        (r): r is PromiseFulfilledResult<ReferenceDataResponse> => r.status === "fulfilled",
    );

    if (successes.length === 0) {
        // Every target fetch failed: rethrow (the first failure) so the
        // widget shows the same error degrade as the self-target path.
        throw (settled[0] as PromiseRejectedResult).reason;
    }

    const byValue = new Map<string, ReferenceOption>();
    let truncated = false;
    let minTtlSeconds: number | undefined;
    for (const { value: response } of successes) {
        truncated = truncated || !!response.truncated;
        const ttl = response.cache_ttl_seconds ?? 300;
        minTtlSeconds = minTtlSeconds === undefined ? ttl : Math.min(minTtlSeconds, ttl);
        for (const option of response.options ?? []) {
            if (!byValue.has(option.value)) byValue.set(option.value, option);
        }
    }

    return {
        options: Array.from(byValue.values()),
        cache_ttl_seconds: minTtlSeconds ?? 300,
        truncated,
        targetCount: targetIds.length,
    };
}

/** Provider-target equivalent of fetchReferenceOptions: the matching providers
 * of the Routes into `destinationIntegrationId`, fanned out and unioned. */
export async function fetchProviderReferenceOptions(
    token: string,
    destinationIntegrationId: string,
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { targetCount: number }> {
    const providerIds = await resolveProviderTargets(token, destinationIntegrationId, actionValue);
    return fetchLinkedReferenceOptions(token, providerIds, actionValue, params);
}

/** Destination-target equivalent of fetchReferenceOptions: the matching
 * destinations of `providerIntegrationId`'s connection, fanned out and unioned. */
export async function fetchDestinationReferenceOptions(
    token: string,
    providerIntegrationId: string,
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { targetCount: number }> {
    const destinationIds = await resolveDestinationTargets(token, providerIntegrationId, actionValue);
    return fetchLinkedReferenceOptions(token, destinationIds, actionValue, params);
}
